// import config from "./webConfig.json";

const host = process.env.REACT_APP_HOST || "http://softcode.creality.com";

let isRefreshing = false;
let failedQueue = [];

const getStore = () => {
    const m = require('../redux/store/index');
    return m.default || m;
};

const getActions = () => {
    return require('../redux/actions/index');
};

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const refreshAccessToken = async () => {
    const response = await fetch(`${host}/knights/auth/refresh`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.status === 200) {
        const data = await response.json();
        if (data.accessToken) {
            const store = getStore();
            const actions = getActions();
            store.dispatch(actions.setAccessToken(data.accessToken));
            return data.accessToken;
        }
    }

    throw new Error('Refresh failed');
};

const handleAuthFailure = () => {
    const store = getStore();
    const actions = getActions();
    store.dispatch(actions.clearAccessToken());
    store.dispatch(actions.userLogOut());
    window.location = "/login";
};

export const fetchCall = async (
    apiUrl,
    apiSuccessMethod,
    apiFailMethod,
    method = "Get",
    body
) => {
    let accessToken = null;
    try {
        const store = getStore();
        const state = store.getState();
        accessToken = state.appPermissions?.accessToken;
    } catch (e) {
        // Store not ready yet - this is OK during initial login
    }

    const makeRequest = (token) => {
        const headers = {
            "Content-Type": "application/json",
        };
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }

        const options = {
            method: method,
            mode: "cors",
            cache: "no-cache",
            credentials: "include",
            withCredentials: true,
            signal: AbortSignal.timeout(600000),
            headers: headers,
            body: body,
        };

        return fetch(apiUrl, options);
    };

    if (!accessToken) {
        if (isRefreshing) {
            try {
                accessToken = await new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                });
            } catch (e) {
                // Refresh failed, proceed without token
            }
        } else {
            isRefreshing = true;
            try {
                accessToken = await refreshAccessToken();
                processQueue(null, accessToken);
            } catch (e) {
                processQueue(e, null);
            } finally {
                isRefreshing = false;
            }
        }
    }

    try {
        let response = await makeRequest(accessToken);

        if (response.status === 401) {
            if (isRefreshing) {
                try {
                    const newToken = await new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });
                    response = await makeRequest(newToken);
                } catch (queueErr) {
                    handleAuthFailure();
                    return;
                }
            } else {
                isRefreshing = true;

                try {
                    const newToken = await refreshAccessToken();
                    processQueue(null, newToken);
                    response = await makeRequest(newToken);
                } catch (refreshErr) {
                    processQueue(refreshErr, null);
                    handleAuthFailure();
                    return;
                } finally {
                    isRefreshing = false;
                }
            }
        }

        if (response.status === 200) {
            const responseData = await response.json();
            apiSuccessMethod(responseData);
        } else {
            if (response.status === 401) {
                handleAuthFailure();
            } else {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    errorData = { message: "Request failed" };
                }
                apiFailMethod(errorData);
            }
        }
    } catch (error) {
        console.error("fetchCall error:", error);
        apiFailMethod(error);
    }
};

export const fetchCallFormData = async (
    apiUrl,
    apiSuccessMethod,
    apiFailMethod,
    method = "POST",
    body
) => {
    let accessToken = null;
    try {
        const store = getStore();
        const state = store.getState();
        accessToken = state.appPermissions?.accessToken;
    } catch (e) {
        // Store not ready yet
    }

    const headers = {};
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const options = {
        method: method,
        mode: "cors",
        cache: "no-cache",
        credentials: "include",
        withCredentials: true,
        headers: headers,
        body: body,
    };
    fetch(apiUrl, options)
        .then(async (res) => {
            if (res.status === 200) {
                apiSuccessMethod(res);
            } else {
                if (res.status === 401) {
                    window.location = "/login";
                } else {
                    const response = await res.json();
                    apiFailMethod(response);
                }
            }
        })
        .catch((error) => {
            apiFailMethod(error);
        });
};

export const fetchCallFile = async (
    apiUrl,
    apiSuccessMethod,
    apiFailMethod,
    method = "POST",
    body
) => {
    let accessToken = null;
    try {
        const store = getStore();
        const state = store.getState();
        accessToken = state.appPermissions?.accessToken;
    } catch (e) {
        // Store not ready yet
    }

    const headers = {
        "Content-Type": "application/json",
    };
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const options = {
        method: method,
        mode: "cors",
        cache: "no-cache",
        credentials: "include",
        withCredentials: true,
        headers: headers,
        body: body,
    };
    fetch(apiUrl, options)
        .then(async (res) => {
            if (res.status === 200) {
                const blob = await res.blob();
                apiSuccessMethod(blob);
            } else {
                if (res.status === 401) {
                    window.location = "/login";
                } else {
                    try {
                        const response = await res.json();
                        apiFailMethod(response);
                    } catch (e) {
                        apiFailMethod({ message: "An error occurred while downloading the file" });
                    }
                }
            }
        })
        .catch((error) => {
            apiFailMethod(error);
        });
};

export const url = {
  addProuctUrl: `${host}/knights/products/add/`,
  getDepartmentProductSearch: `${host}/knights/products/getDepartmentProductSearch/`,
  updateProuctUrl: `${host}/knights/products/update/`,
  getProductUrl: `${host}/knights/products/getAll`,
  getProductByCategory: `${host}/knights/products/getByCategory`,
  getProductImage: `${host}/knights/products/get_image/`,
  migrateProductImages: `${host}/knights/products/migrate-images`,
  getProductForSelect: `${host}/knights/products/select/`,
  getCompanyUrl: `${host}/knights/company/getAll/`,
  addCategoryUrl: `${host}/knights/category/add/`,
  deleteRoles: `${host}/knights/roles/delete/`,
  getDebtDetails: `${host}/knights/customer_invoice_debt/getAll/`,
  insertPaidRow: `${host}/knights/customer_invoice_debt/add/`,
  debtBulkAdd: `${host}/knights/customer_invoice_debt/add_bulk/`,
  add_supplier: `${host}/knights/supplier/add/`,
  get_supplier: `${host}/knights/supplier/getAll/`,
  getSupplier: `${host}/knights/supplier/get`,
  update_supplier: `${host}/knights/supplier/update/`,
  delete_supplier: `${host}/knights/supplier/delete/`,
  getSupplierOptions: `${host}/knights/supplier/options/`,
  getCustomerInvoice: `${host}/knights/customer_invoice/getAll/`,
  getOneInvoice: `${host}/knights/customer_invoice/get/`,
  getFilterCustomerInvoice: `${host}/knights/customer_invoice/filter/`,
  /**
   * customer
   */
  addCustomer: `${host}/knights/customer/add/`,
  getCustomer: `${host}/knights/customer/get`,
  getAllCustomer: `${host}/knights/customer/getAll`,
  getCustomerOptions: `${host}/knights/customer/options/`,
  updateCustomer: `${host}/knights/customer/update/`,
  deleteCustomer: `${host}/knights/customer/delete/`,
  customerClienteling: `${host}/knights/customer/`,
  /**
   * supplier invoice
   */
  getSupplierInvoiceProduct: `${host}/knights/supplier_invoice/product`,
  get_supplier_invoice: `${host}/knights/supplier_invoice/getAll`,
  get_filter_supplier_invoice: `${host}/knights/supplier_invoice/filter/`,
  add_supplier_invoice: `${host}/knights/supplier_invoice/add/`,
  delete_supplier_invoice: `${host}/knights/supplier_invoice/delete/`,
  /**
   * supplier debt
   */

  insert_supplier_paid_row: `${host}/knights/supplierInvoiceDebt/add/`,
  get_supplier_debt_detials: `${host}/knights/supplierInvoiceDebt/getAll/`,
  getDebtSupplier: `${host}/knights/supplierInvoiceDebt/getAllDebt/`,
  getDebtSupplierInvoice: `${host}/knights/supplierInvoiceDebt/getInvoiceDebt/`,
  addDebtSupplierBulk: `${host}/knights/supplierInvoiceDebt/addBulk/`,
  //
  add_service: `${host}/knights/service/add/`,
  getCustomerWithPhone: `${host}/knights/customer/getCustomerWithPhone`,
  get_service: `${host}/knights/service/getAll`,
  get_Service_Settings: `${host}/knights/service_serttings/getAll/`,
  get_Permission_roles: `${host}/knights/permissions_roles/getAll/`,
  add_setting_fileds: `${host}/knights/settings_adding_feilds/add/`,
  add_setting_currency: `${host}/knights/settings_adding_currency/add/`,
  addliraRound: `${host}/knights/settings/update/lira_round`,

  getProductByBarcode: `${host}/knights/products/WithBarcode/`,
  findByBarcodeGlobal: `${host}/knights/products/findByBarcodeGlobal/`,
  getProductById: `${host}/knights/products/getById/`,
  add_role_permission: `${host}/knights/permissions/addRolePermission/`,
  get_customer_invoice_Stat: `${host}/knights/Stats/invocie/`,
  update_service: `${host}/knights/service/update/`,
  delete_position_employee: `${host}/knights/User_position/delete/`,
  deleteService: `${host}/knights/service/delete/`,
  getDebtCustomer: `${host}/knights/customer_invoice_debt/getAll/`,
  getDebtInvoice: `${host}/knights/customer_invoice_debt/getInvoiceDebt/`,
  add_serive: `${host}/knights/settings_adding_service/add/`,
  updateServiceSetting: `${host}/knights/settings_adding_service/update/`,
  get_service_with_setting: `${host}/knights/service/getAllServiceWithSetting/`,
  add_service_settings: `${host}/knights/service_settings/add/`,
  getAppSetting: `${host}/knights/settings/getAll/`,
  updatePricingConfig: `${host}/knights/settings/update/pricing_config`,
  getPublicSettings: `${host}/knights/settings/public`,
  get_category_parent: `${host}/knights/category/getParent/`,
  update_price_for_gold: `${host}/knights/updateGoldPrice/`,
  add_supplier_invoice_gold: `${host}/knights/supplierInvoiceGold/add/`,
  stat_get_most_sold_product: `${host}/knights/Stats/MostsoldProduct/`,
  set_main_price: `${host}/knights/settings_add_main_price/add/`,
  set_commission: `${host}/knights/settings_add_commission/add/`,
  stat_most_sold_emp: `${host}/knights/Stats/MostsoldEmp/`,
  getProductWithoutBarcode: `${host}/knights/products/getAllWithoutBarcode/`,
  deleteProductUrl: `${host}/knights/products/delete/`,
  getCategoryUrl: `${host}/knights/category/getAll/`,
  getCategoryDropDown: `${host}/knights/category/categoryDropDown`,
  getCategoryRoot: `${host}/knights/category/getAllRoot/`,
  getCategoryWithoutParentUrl: `${host}/knights/category/getWithoutParent/`,
  getDistinctBrandsUrl: `${host}/knights/brand/getBrand`,
  getBrandDeleted: `${host}/knights/brand/getBrandDeleted`,
  addBrandUrl: `${host}/knights/brand/setBrand`,
  getColorDeleted: `${host}/knights/color/getColorDeleted`,
  getDistinctColorsUrl: `${host}/knights/color/getColor`,
  addColorUrl: `${host}/knights/color/setColor`,
  getDistinctSizesUrl: `${host}/knights/size/getSize`,
  getSizeDeleted: `${host}/knights/size/getSizeDeleted`,
  addSizeUrl: `${host}/knights/size/setSize`,
  updateCategoryUrl: `${host}/knights/category/update/`,
  deleteCategoryUrl: `${host}/knights/category/delete/`,
  restoreCategoryUrl: `${host}/knights/category/restore/`,
  reorderCategoryUrl: `${host}/knights/category/reorder/`,
  addEmployeePosition: `${host}/knights/User_position/add/`,
  getEmployeePosition: `${host}/knights/User_position/getAll/`,
  addPermission: `${host}/knights/permissions/add/`,
  updatePermission: `${host}/knights/permissions/update/`,
  getPermission: `${host}/knights/permissions/getAll/`,
  deletePermission: `${host}/knights/permissions/delete/`,
  getPermissionCategory: `${host}/knights/PermissionCategory/getAll/`,
  addPermissionCategory: `${host}/knights/PermissionCategory/add/`,
  updatePermissionCategory: `${host}/knights/PermissionCategory/update/`,
  deletePermissionCategoty: `${host}/knights/PermissionCategory/delete/`,
  updateEmployeePosition: `${host}/knights/User_position/update/`,
  addRoles: `${host}/knights/roles/add/`,
  getRoles: `${host}/knights/roles/getAll/`,
  updateRoles: `${host}/knights/roles/update/`,

  /**
   * Customer Invoice
   */
  saveCustomerInvoice: `${host}/knights/customer_invoice/add/`,
  sendInvoiceWhatsApp: `${host}/knights/customer_invoice/send-whatsapp`,
  closeCustomerInvoice: `${host}/knights/customer_invoice/close/`,
  deleteCustomerInvoice: `${host}/knights/customer_invoice/delete`,
  getSavedCustomerInvoice: `${host}/knights/customer_invoice/get/saved/`,
  getDraftInvoce: `${host}/knights/customer_invoice/get_draft/`,
  getCustomerInvoiceById: `${host}/knights/customer_invoice/`,
  deleteCustomerInvoicePdf: `${host}/knights/static/delete_invoice_pdf`,
  setCost: `${host}/knights/cost/add/`,
  deleteCost: `${host}/knights/cost/delete/`,
  updateCost: `${host}/knights/cost/update/`,
  getCost: `${host}/knights/cost/get`,
  getProductFilterTable: `${host}/knights/products/tableFilter`,
  printInvoice: `${host}/knights/customer_invoice/print/`,
  updateChargesType: `${host}/knights/settings_charges_type/update/`,
  getServiceFilterTable: `${host}/knights/service/tableFilter`,
  getCostFilterTable: `${host}/knights/cost/tableFilter`,
  printBarCode: `${host}/knights/print/barcode`,

  geteFund: `${host}/knights/fund/getFund`,
  getFilterFund: `${host}/knights/fund/getFilterFund/`,
  setExchange: `${host}/knights/fund/exchange/`,
  getLineChart: `${host}/knights/Stats/getLineChart/`,
  getProfit: `${host}/knights/Stats/getProfit/`,
  getProfitFilter: `${host}/knights/Stats/getProfitFilter/`,
  checkBarCode: `${host}/knights/products/checkBarCode/`,

  getPrinterList: `${host}/knights/print/get/`,
  updateInvoicePrinter: `${host}/knights/invoice/change_printer/`,
  updateSecondInvoicePrinter: `${host}/knights/invoice/change_printer/second`,
  updateBarcodePrinter: `${host}/knights/barcode/change_printer/`,
  getUnitProduct: `${host}/knights/products/getUnitProduct/`,
  getProductStat: `${host}/knights/products/getStat`,
  getImage: `${host}/knights/setting/images/`,
  setImages: `${host}/knights/setting/images/update`,
  getSupplierProduct: `${host}/knights/products/get_supplier_products/`,
  deleteBrand: `${host}/knights/brand/delete`,
  deleteColor: `${host}/knights/color/delete`,
  deleteSize: `${host}/knights/size/delete`,
  barcodeFields: `${host}/knights/setting/update_barcode`,
  getReturnProduct: `${host}/knights/return_product/get/`,
  setReturnProduct: `${host}/knights/return_product/add/`,
  getInvoceName: `${host}/knights/customer_invoice/get/generate/`,
  /**
   * Products
   */
  exportProductData: `${host}/knights/products/export`,

  /**
   * Barcode Template
   */
  addBarcodeTemplate: `${host}/knights/barcode_template/add`,
  updateBarcodeTemplate: `${host}/knights/barcode_template/update`,
  getBarcodeTemplates: `${host}/knights/barcode_template/getAll`,
  getBarcodeTemplateById: `${host}/knights/barcode_template/get/`,
  getBarcodeTemplateImage: `${host}/knights/barcode_template/get_image`,
  deleteBarcodeTemplate: `${host}/knights/barcode_template/delete/`,

  /**
   * Invoice Template
   */
  addInvoiceTemplate: `${host}/knights/invoice_template/add`,
  updateInvoiceTemplate: `${host}/knights/invoice_template/update`,
  getInvoiceTemplates: `${host}/knights/invoice_template/getAll`,
  getInvoiceTemplateById: `${host}/knights/invoice_template/get/`,
  getDefaultInvoiceTemplate: `${host}/knights/invoice_template/getDefault`,
  deleteInvoiceTemplate: `${host}/knights/invoice_template/delete`,
  setDefaultInvoiceTemplate: `${host}/knights/invoice_template/setDefault`,
  uploadInvoiceLogo: `${host}/knights/invoice_template/uploadLogo`,

  /**
   * license
   */
  setAppLicense: `${host}/knights/license/add`,
  checkAppLicense: `${host}/knights/license/check`,
  downloadAppLicense: `${host}/knights/license/download`,
  /**
   * print setting
   */
  setPrintSetting: `${host}/knights/print/setting/insert`,
  updatePrintSetting: `${host}/knights/print/setting/update`,
  deletePrintSetting: `${host}/knights/print/setting/delete`,
  getPrintSetting: `${host}/knights/print/setting/get`,
  getPrintSettingImage: `${host}/knights/print/setting/get_image`,
  printBarcode: {
    getBarcode: `${host}/knights/barcode_template/getAll`,
    printBarcode: `${host}/knights/barcode_template/print`,
    deleteBarcode: `${host}/knights/barcode_template/delete`,
    updateBarcode: `${host}/knights/barcode_template/update`,
    addBarcode: `${host}/knights/barcode_template/add`,
    getBarcodeImage: `${host}/knights/barcode_template/get_image`,
    getBarcodeById: `${host}/knights/barcode_template/get/`,
  },
  /**
   * employee
    */
  employee_check_passowrd: `${host}/knights/employee/checkPassword/`,
  employeeLogout: `${host}/knights/employee/logout/`,
  authRefresh: `${host}/knights/auth/refresh`,
  authLogout: `${host}/knights/auth/logout`,
  authLogoutAll: `${host}/knights/auth/logout-all`,
  authVerify: `${host}/knights/auth/verify`,
  addEmployeeUrl: `${host}/knights/employee/add`,
  getEmployeeUrl: `${host}/knights/employee/getAll/`,
  updateEmployeeUrl: `${host}/knights/employee/update/`,
  deleteEmployeeUrl: `${host}/knights/employee/delete/`,
  getAdminsUrl: `${host}/knights/employee/getAdmins`,
  getEmployeeCost: `${host}/knights/employee/getEmployeeCrash`,
  getUserPrinters: `${host}/knights/employee/get_printers/`,
  changePassword: `${host}/knights/employee/change_password/`,
  resetPassword: `${host}/knights/employee/reset_password/`,
  getTheme: `${host}/knights/employee/get_theme/`,
  setTheme: `${host}/knights/employee/update_theme/`,
  setShowInvoiceCategory: `${host}/knights/employee/updateShowInvoiceCategory/`,
  getShowInvoiceCategory: `${host}/knights/employee/getShowInvoiceCategory/`,
  updateLang: `${host}/knights/employee/updateLang`,
  getSelectEmployee: `${host}/knights/employee/getSelect/`,
  getEmployeesList: `${host}/knights/employee/get_list/`,
  setEmployeePin: `${host}/knights/employee/set_pin/`,
  addEmployeeAdvance: `${host}/knights/employee/add_advance/`,
  getEmployeeAdvances: `${host}/knights/employee/get_advances/`,
  payEmployeeSalary: `${host}/knights/employee/pay_salary/`,
  getSalaryPayouts: `${host}/knights/employee/get_salary_payouts/`,
  togglePinAutoChange: `${host}/knights/employee/toggle_pin_auto_change/`,

  /**
   * Online Order
   */
  getOnlineOrderList: `${host}/knights/order/getOnline`,
  updateOnlineOrderDelivery: `${host}/knights/order/update/delivery`,
  deleteOnlineOrder: `${host}/knights/order/delete`,
  payOnlineOrder: `${host}/knights/order/pay`,
  getSelect: `${host}/knights/employee/getSelect/`,
  /**
   * products
   */
  getProductTimeline: `${host}/knights/product/timeline`,

  department: {
    get: `${host}/knights/department/get`,
    set: `${host}/knights/department/set`,
    update: `${host}/knights/department/update`,
    delete: `${host}/knights/department/delete`,
  },
  departmentDebt: {
    get: `${host}/knights/department_debt/get`,
    getDebtRows: `${host}/knights/department_debt/getInvoiceDebt/`,
    set: `${host}/knights/department_debt/add/`,
  },
  departmentInvoice: {
    set: `${host}/knights/department_invoice/set_invoice`,
    generateCustomerInvoice: `${host}/knights/department_invoice/add/generate`,
    getInvoice: `${host}/knights/department_invoice/get`,
  },
  products: {
    getDepartmentProduct: `${host}/knights/products/getDepartmentProduct/`,
    getAllDepartmentProduct: `${host}/knights/products/getAllDepartmentProduct/`,
    getInventoryProduct: `${host}/knights/products/getProductInventory`,
    updateInventoryProduct: `${host}/knights/products/updateProductInventory`,
    updateAllQty: `${host}/knights/products/updateAllQty`,
    getExpireProdcut: `${host}/knights/products/get/expire`
  },
  openFund: {
    openFund: `${host}/knights/open_fund/open/`,
    closeFund: `${host}/knights/open_fund/close/`,
    getOpenFund: `${host}/knights/open_fund/get/open/`,
    getFund: `${host}/knights/open_fund/get/`,
    setExchange: `${host}/knights/fund/add/exchange/`,
    getIfEmpOpenFund: `${host}/knights/open_fund/getIfEmpOpenFund/`,
    getSupplierOpenFund: `${host}/knights/open_fund/get/supplier/`,
  },
  fund: {
    getLast: `${host}/knights/fund/get/last/`,
    setExchange: `${host}/knights/fund/add/exchange/`,
    getFund: `${host}/knights/fund/get/`,
    getFilterFund: `${host}/knights/fund/getFilterFund/`,
    addToFundBox: `${host}/knights/fund/add/`,
    transferVault: `${host}/knights/fund/transfer/vault/`,
    transferVaultToVault: `${host}/knights/fund/transfer/vault-to-vault/`,
    get: `${host}/knights/fund/get/`,
  },
  box: {
    set: `${host}/knights/box/set/`,
    update: `${host}/knights/box/update/`,
    getAll: `${host}/knights/box/getAll/`,
    delete: `${host}/knights/box/delete/`,
    getById: `${host}/knights/box/getById/`,
    notOpenedBox: `${host}/knights/box/not_open_box/`,
    getMoneyValut: `${host}/knights/box/get_money_valut/`,
  },
  transferBox: {
    set: `${host}/knights/transfer_box/set/`,
    getTransfer: `${host}/knights/transfer_box/get_transfer/`,
  },
  printLocal: {
    getPrinter: `http://localhost:2733/getPrinters`,
    printDirect: `http://localhost:2733/printDirect`,
  },
  inventory: {
    stat_most_sold_emp: `${host}/knights/inventory/get/stat/emp`,
    stat_get_most_sold_product: `${host}/knights/inventory/get/stat/product`,
    getLineChart: `${host}/knights/stats/getLineChart`,
    productStat: `${host}/knights/inventory/get/stat/`,
  },
  report: {
    printDailyReport: `${host}/knights/reports/fund/printDailyReport/`,
    dailyReport: {
      generate: `${host}/knights/reports/daily/generate`,
      download: `${host}/knights/reports/daily/download`,
      config: `${host}/knights/reports/daily/config`,
      saveConfig: `${host}/knights/reports/daily/config`,
      print: `${host}/knights/reports/daily/print`,
      quickSend: `${host}/knights/reports/daily/generate`,
      languages: `${host}/knights/reports/daily/languages`,
      printers: `${host}/knights/reports/daily/printers`,
      schedule: `${host}/knights/reports/daily/schedule`,
      schedules: `${host}/knights/reports/schedules`,
    },
    customer: {
      generate: `${host}/knights/reports/customer`,
      download: `${host}/knights/reports/customer/download`,
    },
    supplier: {
      generate: `${host}/knights/reports/supplier`,
      download: `${host}/knights/reports/supplier/download`,
    },
    fund: {
      generate: `${host}/knights/reports/fund-report`
    },
    vault: {
      transactions: `${host}/knights/reports/vault/transactions`,
      download: `${host}/knights/reports/vault/download`,
    },
    yearly: {
      generate: `${host}/knights/reports/yearly`,
      download: `${host}/knights/reports/yearly/download`,
    },
    monthly: {
      generate: `${host}/knights/reports/monthly`,
      download: `${host}/knights/reports/monthly/download`,
    },
    commission: `${host}/knights/reports/employee-commissions`,
  },
  whatsapp: {
    baseUrl: `${host}/knights/whatsapp`,
    status: `${host}/knights/whatsapp/status`,
    checkNumber: `${host}/knights/whatsapp/checkNumber`,
    getProfilePic: `${host}/knights/whatsapp/getProfilePic`,
    sendProducts: `${host}/knights/whatsapp/sendProducts`,
    sendReminders: `${host}/knights/whatsapp/send-reminders`,
    config: `${host}/knights/whatsapp/config`,
    debtCustomers: `${host}/knights/whatsapp/debt-customers`,
    customerReminder: `${host}/knights/whatsapp/customer-reminder`,
    sendReport: `${host}/knights/whatsapp/sendReport`,
    sendDebtReminders: `${host}/knights/whatsapp/sendDebtReminders`,
    getQueue: `${host}/knights/whatsapp/getQueue`,
    retryMessage: `${host}/knights/whatsapp/retryMessage`,
    deleteMessage: `${host}/knights/whatsapp/deleteMessage`,
    checkWAContacts: `${host}/knights/customer/wa_contacts_check`,
    importWAContacts: `${host}/knights/customer/wa_contacts_import`,
    notifyOwner: `${host}/knights/whatsapp/notify-owner`,
    sendReceipt: `${host}/knights/customer_invoice/send-receipt-whatsapp`,
  },
  userSetting: {
    setExpirePeriod: `${host}/knights/settings/update/expire_period`,
    updateCardSettings: `${host}/knights/settings/update/card_settings`,
  },
  audit: {
    list: `${host}/knights/audit/list`
  },
  integration: {
    config: `${host}/knights/integration/config`,
    syncAttributes: `${host}/knights/integration/sync/attributes`,
    syncProducts: `${host}/knights/integration/sync/products`,
    syncProductsChunked: `${host}/knights/integration/sync/products/chunked`,
    syncProductsChunk: `${host}/knights/integration/sync/products/chunk`,
    runSync: `${host}/knights/integration/sync/products/run`,
    syncStatus: `${host}/knights/integration/sync/products/run/status`,
    uploadImage: `${host}/knights/integration/upload-image`,
    updateDescription: `${host}/knights/integration/update-description`,
    getProducts: `${host}/knights/integration/products`,
    filterProducts: `${host}/knights/integration/products/filter`,
    removeProduct: `${host}/knights/integration/remove-product`,
    resyncProduct: `${host}/knights/integration/resync-product`,
    resyncAll: `${host}/knights/integration/resync-all`,
    removeAllProducts: `${host}/knights/integration/remove-all-products`,
    passThroughProducts: `${host}/knights/products/pass-through`,
  },
  websiteOrder: {
    get: `${host}/knights/website_order/get`,
    status: `${host}/knights/website_order/status`,
    delete: `${host}/knights/website_order/delete`,
    accept: `${host}/knights/website_order/accept`,
    pay: `${host}/knights/website_order/pay`,
    getItems: (id) => `${host}/knights/website_order/items/${id}`,
  },

  boutique: {
    updateFeatures: `${host}/knights/settings/pos-features`,
  }
};

export const jsonPropertyType = [
  { label: "Text", value: "string", type: "text" },
  { label: "Word", value: "char", type: "text" },
  { label: "Number", value: "number", type: "number" },
  // {label:'Image',value:'image'},
];

export const currencyList = {
  $: "$",
  lira: "LBP",
};

export const fieldType = [
  { label: "Text", value: "text" },
  { label: "Number", value: "number" },
  { label: "CheckBox", value: "checkbox" },
];
export const operator = [
  { label: "Multiplication", value: "multiplication" },
  { label: "Divide", value: "divide" },
];

export const getAmountTypeList = (t) => {
  return [
    { label: t("product.unit"), value: "unit" },
    { label: t("product.weight"), value: "weight" },
    { label: t("product.box") || "Box", value: "box" },
  ];
};
export const amountTypeList = [
  { label: "Unit", value: "unit" },
  { label: "Weight", value: "weight" },
  { label: "Box", value: "box" },
];

export const getOperatorValue = (value, operator, price) => {
  if (operator === "multiplication") {
    return value / price;
  } else {
    return value * price;
  }
};

export const defaultEmployeeTheme = {
  primary: {
    bg: "#542643",
    font: "#fbdeb6",
  },
  secondary: {
    bg: "#9a0074",
    font: "#ff509cd6",
  },
  sideBar: {
    bg: "#542643",
    font: "#fbdeb6",
    collapseFont: "#542643",
    collapseBg: "#fbdeb6",
  },
  mainTable: {
    headerBg: "#542643",
    headerFont: "#fbdeb6",
    footerBg: "#d84be712",
    footerFont: "#4f3750d6",
    hoverRow: "#5426437d",
  },
  detailTable: {
    headerBg: "#f6a3ff40",
    headerFont: "#4f3750d6",
  },
  requiredFields: {
    bg: "#54264333",
  },
};

export const dataType = {
  pdf: "PDF",
  raw: "RAW",
};

export const printerLanguage = [
  { label: 'ZPL', value: 'zpl' },
  { label: 'EPL', value: 'epl' },
  { label: 'TSPL', value: 'tspl' },
]